import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CCard, CCardBody, CFormSelect, CSmartTable } from '@coreui/react-pro'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import UserService from 'src/service/UserService'
import GameListingModal from '../GameAccount/GameListingModal'
import './HubMember.css'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '1', label: 'Activated' },
  { value: '0', label: 'Deactivated' },
]

const columns = [
  { label: 'Member', key: 'full_name', filter: false, sorter: true },
  { label: 'Email', key: 'email', filter: false, sorter: false },
  { label: 'Phone', key: 'phone', filter: false, sorter: false },
  { label: 'Registered', key: 'created_at', filter: false, sorter: true },
  { label: 'Status', key: 'is_active', filter: false, sorter: false },
  // { key: 'actions', label: 'Actions', filter: false, sorter: false },
]

const getBadge = (status) => {
  switch (status) {
    case 1:
      return 'success'
    case 0:
      return 'warning'
    default:
      return 'primary'
  }
}

const HubMember = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [statusFilter, setStatusFilter] = useState('')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()

  const [modalIsOpen, setIsOpen] = useState(false)
  const [userDataListing, setUserDataListing] = useState([])

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value)
    setActivePage(1)
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (statusFilter !== '') params.append('is_active', statusFilter)
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.hubUserListing(offset, itemsPerPage, activePage, params, false, true).then(
      (result) => {
        setUsers(result.data)
        setLoading(false)
      },
    )
  }, [activePage, columnFilter, columnSorter, itemsPerPage, statusFilter])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  function openFromParent(accountId) {
    UserService.getGameUser(accountId).then((result) => {
      setUserDataListing(result.data)
    })
    setIsOpen(true)
  }

  const changeAccountStatus = (userId, status) => {
    UserService.changeAccountStatus({ userId, status }).then((result) => {
      ToastComponent(result.message, 'success')
      fetchData()
    })
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Hub Members</h1>
        <p className="ge-page-subtitle">Manage the members who have joined your hub.</p>
      </div>

      <CCard className="hm-panel">
        <CCardBody>
          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={handleFieldChange}
            value={columnFilter?.[searchField] || ''}
            onSearch={applySearch}
            placeholder={`Search by ${activeSearchLabel}...`}
            trailing={
              <CFormSelect
                size="sm"
                style={{ width: 170 }}
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                aria-label="Filter by status"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </CFormSelect>
            }
          />
          <div className="ge-datatable ge-datatable--no-select">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              scopedColumns={{
                full_name: (item) => <td className="fw-semibold">{item.full_name}</td>,
                created_at: (item) => <td>{moment(item.created_at).format('D.MM.YYYY')}</td>,
                is_active: (item) => (
                  <td>
                    <CBadge
                      color={getBadge(item.is_active)}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.is_active === 1 ? 'Activated' : 'Deactivated'}
                    </CBadge>
                  </td>
                ),
                // actions: (item) => (
                //   <td>
                //     <RowActionsMenu
                //       actions={[
                //         {
                //           label: 'Login',
                //           onClick: () =>
                //             window.open(
                //               `${process.env.REACT_APP_FRONT_URL}adminLogin/${item.id}`,
                //               '_blank',
                //             ),
                //         },
                //         item.user_role_id === 3 && {
                //           label: 'Games',
                //           onClick: () => openFromParent(item.id),
                //         },
                //         item.user_role_id === 3 &&
                //           item.is_active === 1 && {
                //             label: 'Hide Hub',
                //             onClick: () => changeAccountStatus(item.id, 0),
                //           },
                //         item.user_role_id === 3 &&
                //           item.is_active === 0 && {
                //             label: 'Show Hub',
                //             onClick: () => changeAccountStatus(item.id, 1),
                //           },
                //       ].filter(Boolean)}
                //     />
                //   </td>
                // ),
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              noItemsLabel="No members have joined this hub yet."
              pagination={{ external: true }}
              paginationProps={{
                activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>

      <GameListingModal
        IsModalOpened={modalIsOpen}
        onCloseModal={setIsOpen}
        userDataListing={userDataListing}
      />
    </>
  )
}

export default HubMember
